import { humanizeReferenceDifferenceText } from '../../lib/referencePresentation';

export const REFERENCE_WORKER_INPUT_STATUSES = new Set([
  'not_declared',
  'needs_new_input',
  'rejected',
]);

const CV_REUPLOAD_STATUSES = new Set([
  'rejected',
  'replacement_requested',
  'replacement_required',
  'reupload_required',
]);

/** Applicant only sees CV when they must upload or replace — not for viewing after admin verification. */
export function applicantCvNeedsAction(cvStatus, { unresolvedNotifications = [] } = {}) {
  if (unresolvedNotifications.length > 0) return true;
  if (!cvStatus) return false;
  const status = String(cvStatus.cv_status || '').toLowerCase();
  if (cvStatus.has_cv) {
    return Boolean(cvStatus.replacement_required || CV_REUPLOAD_STATUSES.has(status));
  }
  return Boolean(cvStatus.can_upload_cv) && !['under_review', 'uploaded', 'approved'].includes(status);
}

function mismatchNeedsWorkerExplanation(mismatch) {
  if (!mismatch) return false;
  if (mismatch.mismatch_admin_decision === 'accepted') return false;
  const decision = String(mismatch.mismatch_admin_decision || '').toLowerCase();
  if (decision === 'needs_clarification' || decision === 'rejected') return true;
  if (mismatch.explanation_status === 'submitted') return false;
  if (mismatch.needs_explanation === false) return false;
  return true;
}

export function referenceWorkerTaskKind(reference, mismatchByRefNum = null) {
  if (!reference) return null;
  if (reference.can_provide_new) return 'provide_new';

  const refNum = Number(reference.reference_number);
  const mismatch = mismatchByRefNum?.get?.(refNum);
  const adminDecision = String(
    reference.mismatch_admin_decision || mismatch?.mismatch_admin_decision || '',
  ).toLowerCase();
  if (
    reference.requires_mismatch_explanation
    || reference.needs_explanation
    || reference.awaiting_worker_explanation
    || adminDecision === 'needs_clarification'
    || adminDecision === 'rejected'
    || mismatchNeedsWorkerExplanation(mismatch)
  ) {
    return 'mismatch_explanation';
  }

  const status = String(reference.status || '').toLowerCase();
  if (REFERENCE_WORKER_INPUT_STATUSES.has(status)) return 'add_referee';
  return null;
}

export function referenceNeedsWorkerAction(reference, mismatchByRefNum = null) {
  return Boolean(referenceWorkerTaskKind(reference, mismatchByRefNum));
}

export function resolveProfileChecksTask({
  applicationOnFile = true,
  applicationStatus = 'on_file',
  cvNeedsWorkerAction = false,
  cvDisplay = {},
  forms = [],
  references = [],
  mismatchByRefNum = null,
  gapsNeedAction = false,
  employmentHistoryNeedsAction = false,
  isFormWorkerActionRequired,
}) {
  // CS-2: the canonical Application Form requirement (decided on the backend and
  // passed in via applicationOnFile) OUTRANKS every guided-wizard focus. Without
  // this, the wizard would surface CV/references while no application exists.
  if (!applicationOnFile) {
    // possible_historical: more than one earlier application might be this
    // applicant's — the recruitment team must confirm the right one BEFORE
    // anything is linked. Show a calm waiting state, NEVER a blank-form CTA.
    if (applicationStatus === 'possible_historical') {
      return {
        focus: 'application',
        kind: 'application_form',
        subtitle: 'In review',
        title: "We're checking your earlier application",
        description:
          'We found more than one earlier application that might be yours. '
          + "Our team is confirming the right one — there's nothing you need to do.",
        actionKey: 'application_form',
        primaryLabel: null,
        route: null,
        formId: null,
        owner: 'admin',
      };
    }
    const recovering = applicationStatus === 'confirmation_required';
    const restoring = applicationStatus === 'recoverable';
    return {
      focus: 'application',
      kind: 'application_form',
      subtitle: restoring
        ? 'Restoring your application'
        : recovering ? 'Review recovered application' : 'Application required',
      title: restoring
        ? 'Restoring your application'
        : recovering ? 'Review your recovered application' : 'Complete your application',
      description: restoring
        ? 'We found your earlier application and are restoring it for you — no need to fill it in again.'
        : recovering
        ? 'We found information from your earlier application. Please check it and confirm the missing items.'
        : 'We need your application before we can continue with recruitment.',
      actionKey: 'application_form',
      primaryLabel: restoring ? 'Restore application' : recovering ? 'Review application' : 'Continue application',
      route: '/worker/forms/application_form',
      formId: 'application_form',
    };
  }
  if (cvNeedsWorkerAction) {
    const isReplacement = Boolean(
      cvDisplay.replacement_required
      || CV_REUPLOAD_STATUSES.has(String(cvDisplay.cv_status || '').toLowerCase()),
    );
    return {
      focus: 'cv',
      subtitle: isReplacement ? 'CV update' : 'CV',
      title: cvDisplay.title || (isReplacement ? 'Upload replacement CV' : 'Upload your CV'),
      description: cvDisplay.description
        || 'Upload one clear PDF or Word CV covering the last 10 years.',
      actionKey: isReplacement ? 'cv_replacement' : 'cv_upload',
      primaryLabel: cvDisplay.primaryLabel || (isReplacement ? 'Upload replacement' : 'Upload CV'),
      route: '#documents-card',
    };
  }

  const pendingForm = forms.find(
    (form) => form.required !== false && isFormWorkerActionRequired(form.status),
  );
  if (pendingForm) {
    const inProgress = String(pendingForm.status || '').toLowerCase() === 'in_progress';
    return {
      focus: 'form',
      subtitle: inProgress ? 'Continue where you left off' : 'Form to complete',
      title: pendingForm.name,
      description: 'Fill this in and submit for review.',
      actionKey: `form_${pendingForm.id}`,
      primaryLabel: inProgress ? 'Continue form' : 'Start form',
      route: '#forms',
      formId: pendingForm.id,
    };
  }

  if (employmentHistoryNeedsAction) {
    return {
      focus: 'employment_history',
      subtitle: 'Work history',
      title: 'Check your work history',
      description: 'Add any missing roles so your 10-year timeline can be checked.',
      actionKey: 'employment_history_missing',
      primaryLabel: 'Check work history',
      route: '#employment-history',
    };
  }

  const REFERENCE_TASK_PRIORITY = {
    mismatch_explanation: 0,
    provide_new: 1,
    add_referee: 2,
  };
  const refNeeding = [...references]
    .sort((left, right) => {
      const leftKind = referenceWorkerTaskKind(left, mismatchByRefNum) || 'other';
      const rightKind = referenceWorkerTaskKind(right, mismatchByRefNum) || 'other';
      return (REFERENCE_TASK_PRIORITY[leftKind] ?? 9) - (REFERENCE_TASK_PRIORITY[rightKind] ?? 9);
    })
    .find((ref) => referenceNeedsWorkerAction(ref, mismatchByRefNum));
  if (refNeeding) {
    const refNum = refNeeding.reference_number;
    const mismatch = mismatchByRefNum?.get?.(Number(refNum));
    const kind = referenceWorkerTaskKind(refNeeding, mismatchByRefNum);

    if (kind === 'mismatch_explanation') {
      return {
        focus: 'reference',
      subtitle: 'Reference check',
      title: `Reference ${refNum}: ${refNeeding.referee_name || 'explain the difference'}`,
      description: humanizeReferenceDifferenceText(mismatch?.message),
      actionKey: `reference_mismatch_${refNum}`,
      primaryLabel: 'Explain difference',
        route: '#references-section',
        referenceNumber: refNum,
      };
    }

    if (kind === 'provide_new') {
      return {
        focus: 'reference',
        subtitle: 'Referee details needed',
        title: `Reference ${refNum}`,
        description: 'Please add referee details so your application can keep moving.',
        actionKey: `reference_new_${refNum}`,
        primaryLabel: 'Add referee details',
        route: '#references-section',
        referenceNumber: refNum,
      };
    }

    return {
      focus: 'reference',
      subtitle: 'Reference needed',
      title: `Referee ${refNum}`,
      description: 'Add your referee details below so our team can request a reference.',
      actionKey: `reference_${refNum}`,
      primaryLabel: 'Add referee',
      route: '#references-section',
      referenceNumber: refNum,
    };
  }

  if (gapsNeedAction) {
    return {
      focus: 'gaps',
      subtitle: 'Employment history',
      title: 'Explain a gap in your work history',
      description: 'Add a short note for any period not covered by your application.',
      actionKey: 'employment_gaps',
      primaryLabel: 'Explain gaps',
      route: '#employment-gaps-section',
    };
  }

  return { focus: null };
}

export function resolveProfileChecksFocus(args) {
  return resolveProfileChecksTask(args).focus;
}

export function resolveProfileChecksRepairTask({
  cvNeedsWorkerAction = false,
  cvDisplay = {},
  gapsNeedAction = false,
  employmentHistoryNeedsAction = false,
}) {
  return resolveProfileChecksTask({
    cvNeedsWorkerAction,
    cvDisplay,
    forms: [],
    references: [],
    mismatchByRefNum: null,
    gapsNeedAction,
    employmentHistoryNeedsAction,
    isFormWorkerActionRequired: () => false,
  });
}


/** Old drafts from before the no-history fix should not suggest a referee mismatch.
 * The applicant can re-submit their saved referee without an admin cross-check.
 * Real employer differences still need a correction or an explanation.
 */
export function isHistoryOnlyReferenceDraft(mismatch) {
  const codes = mismatch?.reason_codes;
  return Array.isArray(codes)
    && codes.length > 0
    && codes.includes('no_employment_history')
    && codes.every((code) => ['no_employment_history', 'missing_slot_reason'].includes(code));
}
